import { Users, Globe, Brain, Heart, Target, Shield, Zap, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();
  return (
    <div className="max-w-4xl mx-auto space-y-12">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          {t('about.title')}
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          {t('about.subtitle')}
        </p>
      </div>

      {/* Features Grid - moved from HomePage */}
      <div className="grid md:grid-cols-3 gap-8">
        <div className="card text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-100 rounded-full">
              <Zap className="text-primary-600" size={32} />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('home.features.fast.title')}
          </h3>
          <p className="text-gray-600">
            {t('home.features.fast.description')}
          </p>
        </div>

        <div className="card text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-100 rounded-full">
              <Users className="text-primary-600" size={32} />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('home.features.community.title')}
          </h3>
          <p className="text-gray-600">
            {t('home.features.community.description')}
          </p>
        </div>

        <div className="card text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-100 rounded-full">
              <Star className="text-primary-600" size={32} />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {t('home.features.quality.title')}
          </h3>
          <p className="text-gray-600">
            {t('home.features.quality.description')}
          </p>
        </div>
      </div>

      {/* Mission Statement */}
      <div className="card bg-white border-gray-200">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('about.mission.title')}
          </h2>
          <p className="text-lg text-gray-800 max-w-3xl mx-auto">
            {t('about.mission.description')}
          </p>
        </div>
      </div>

      {/* Technology Section */}
      <div className="card">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Brain className="text-primary-600" size={40} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('about.technology.title')}
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('about.technology.nllbTitle')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('about.technology.nllbDescription')}
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• 54 billion parameter multilingual model</li>
              <li>• Trained on diverse, high-quality data</li>
              <li>• Optimized for low-resource language pairs</li>
              <li>• Continuous learning from user feedback</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('about.technology.infrastructureTitle')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('about.technology.infrastructureDescription')}
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Azure Container Apps for scalability</li>
              <li>• Real-time API with sub-second responses</li>
              <li>• Secure data handling and privacy protection</li>
              <li>• 99.9% uptime service level commitment</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Values */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
          {t('about.values.title')}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Target className="text-blue-600" size={28} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('about.values.innovation.title')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('about.values.innovation.description')}
            </p>
          </div>

          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-green-100 rounded-full">
                <Heart className="text-green-600" size={28} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('about.values.community.title')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('about.values.community.description')}
            </p>
          </div>

          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Shield className="text-purple-600" size={28} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('about.values.privacy.title')}
            </h3>
            <p className="text-gray-600 text-sm">
              {t('about.values.privacy.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Beta Information */}
      <div className="card bg-yellow-50 border-yellow-200">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-yellow-900 mb-3">
            🚧 {t('about.beta.title')}
          </h3>
          <p className="text-yellow-800 max-w-3xl mx-auto mb-4">
            {t('about.beta.description')}
          </p>
          <p className="text-yellow-800 max-w-3xl mx-auto">
            {t('about.beta.feedbackImportance')}
          </p>
        </div>
      </div>

      {/* Team & Contact */}
      <div className="card bg-gray-50">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {t('about.contact.title')}
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            {t('about.contact.description')}
          </p>
          <div className="flex justify-center space-x-4">
            <a 
              href="mailto:info@sranslate.nl" 
              className="btn-primary"
            >
              {t('about.contact.contactButton')}
            </a>
            <a 
              href="mailto:info@sranslate.nl?subject=Sranslate%20Feedback" 
              className="btn-secondary"
            >
              {t('about.contact.feedbackButton')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;